import React, { useState } from "react";
import classNames from "classnames";
import { Button } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { NocCorrectionsList } from "./NocCorrectionsList";
import * as styles from "./styles_vanilla.css";
import { type InvalidInstructionsWarningProps } from "./types";
import {
  WARNING_HEADER_TEXT,
  getFirstBulletPoint,
  getSecondBulletPoint,
  isNOCCTypeReturnCode,
  parseNocCorrections,
} from "./utils";

/**
 * InvalidInstructionsWarning component displays an expandable/collapsible warning banner
 * for payment preferences with verification errors. It shows different content based on
 * the return code type. For NOC (C-type) codes, it also displays corrected details
 * and payment information when available. Clicking the payment ID opens a modal with
 * full payment details fetched from the server.
 */
export const InvalidInstructionsWarning: React.FC<InvalidInstructionsWarningProps> = ({
  paymentPref,
  displayNocCorrectionDetails,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (paymentPref.verificationStatus !== "error") {
    return null;
  }

  const firstBulletPoint = getFirstBulletPoint(paymentPref.returnCode);
  const secondBulletPoint = getSecondBulletPoint(
    paymentPref.returnCode,
    paymentPref.codeReason,
  );
  const nocCorrections = parseNocCorrections(paymentPref.nocCorrections);

  return (
    <div
      className={classNames(
        styles.warningContainer,
        isExpanded ? styles.warningContainerExpanded : styles.warningContainerCollapsed,
      )}
    >
      <div className={styles.warningFlex}>
        <Icon
          type="exclamation-triangle"
          color={Colors.Warning700}
          aria-hidden="true"
          data-testid="exclamation-triangle-icon"
          className={styles.warningIcon}
        />
        <div className={styles.warningContent}>
          <Button
            variant="custom"
            onClick={() => setIsExpanded(!isExpanded)}
            className={classNames(
              styles.expandButton,
              isExpanded ? styles.expandButtonExpanded : styles.expandButtonCollapsed,
            )}
            aria-expanded={isExpanded}
            aria-controls="warning-details"
            aria-label={`${isExpanded ? "Collapse" : "Expand"} warning details`}
            icon={isExpanded ? "chevron-up" : "chevron-down"}
            iconPosition="right"
            displayAsLink
          >
            {WARNING_HEADER_TEXT}
          </Button>

          {isExpanded && (
            <ul id="warning-details" className={styles.warningDetails}>
              <li className={styles.warningListItem}>{firstBulletPoint}</li>
              {secondBulletPoint && (
                <li className={styles.warningListItem}>{secondBulletPoint}</li>
              )}
              {displayNocCorrectionDetails && isNOCCTypeReturnCode(paymentPref.returnCode) && (
                <NocCorrectionsList corrections={nocCorrections} />
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
